import Dexie from 'dexie';
import { db, PUSH_INDEX, READ_INDEX, SCHEMA_VERSION } from './db';
import type {
  AttemptRow, ProfileRow, QuarantineRow, RevealRow, SessionRow, SyncTable,
} from './db';
import { supabase } from './auth';
import { looksOffline } from './offline';
import { эпоха } from './epoch';
import {
  отказСоединения, переоткрытьСоединение, сПовторомПриОбрыве, текущееПоколение,
} from './dbReconnect';
import { report } from '../sentry';

/** Размер пачки. Пятьсот строк — это примерно двадцать пять тренировок:
 *  достаточно, чтобы догнать месяц занятий за несколько запросов, и мало,
 *  чтобы отказ посреди отправки не стоил долгого повтора. */
const BATCH = 500;

const TABLES: SyncTable[] = ['attempts', 'reveals', 'sessions'];

export interface SyncOutcome {
  pushed: number;
  pulled: number;
  /** Профили, снесённые локально по авторитетному списку сервера. */
  removed: string[];
  /** Профили, которые сервер не принял из-за занятой пары «цвет и зверёк».
   *  Повторять бесполезно — нужен выбор родителя. */
  lookConflicts: string[];
  /** Пришла строка, записанная приложением новее нашего. Докачка этой
   *  таблицы остановлена: истолковать такую строку нечем, а пропустить её
   *  значит потерять навсегда. */
  needsUpdate: boolean;
  /** Профили, чей обмен встал на отказе, не связанном с сетью. Остальные
   *  дети при этом синхронизируются: один сломанный профиль не должен
   *  замораживать занятия всей семьи. */
  stuck: string[];
  /** Строки, не прошедшие проверку на входе и потому уехавшие в карантин.
   *
   *  Курсор за такую строку двигается — иначе один испорченный ответ
   *  заморозил бы журнал ребёнка навсегда. Но сама строка не выбрасывается:
   *  она ложится в `quarantine` вместе с причиной, а ключ (таблица, профиль,
   *  серверный номер) есть даже когда в содержимом нет годного `id`.
   *
   *  Свойство формулируется так: **каждая серверная строка либо применена,
   *  либо лежит в карантине.** Потеря отложенная, а не безвозвратная.
   *  Отличие от строки более новой версии в том, что та станет читаемой
   *  после обновления приложения, и курсор за неё не двигается вовсе. */
  rejected: number;
  /** Собственные строки, которые сервер отверг НАВСЕГДА и которые поэтому
   *  пропущены, чтобы не морозить журнал.
   *
   *  У докачки такой случай предусмотрен с самого начала — испорченная
   *  серверная строка уезжает в карантин, а курсор через неё
   *  перешагивает. У отправки этого не было, и одна отвергнутая строка
   *  замораживала журнал ребёнка НАВСЕГДА: пачка не проходит, курсор не
   *  двигается, следующий обмен упирается в ту же пачку.
   *
   *  Дорога сюда прямая и предусмотрена самим сервером: он отбраковывает
   *  отметки раньше 2020 года и дальше суток в будущее — то есть занятия,
   *  записанные на планшете со сбитой датой (севшая батарейка сбрасывает
   *  часы). Родитель поправит дату, а те записи не уедут уже никогда.
   *
   *  Пропуск здесь не потеря: местная строка остаётся в журнале и никуда
   *  не девается. Не уезжает только копия — то есть на втором устройстве
   *  этих попыток не будет. */
  notSent: number;
}

const empty = (): SyncOutcome => ({
  pushed: 0, pulled: 0, removed: [], lookConflicts: [], stuck: [], needsUpdate: false, rejected: 0,
  notSent: 0,
});

/** Свой ли ещё обмен, начатый в эпоху `снятая`: не стирали ли с тех пор
 *  устройство. Зачем это нужно — в `epoch.ts`.
 *
 *  Сверка стоит в местах трёх родов, и каждый род закрывает своё:
 *
 *  - ВНУТРИ каждой транзакции записи. Ответ сервера, пришедший после
 *    стирания, не ложится в базу. Именно внутри, а не перед: сверка до
 *    транзакции оставила бы окно между собой и записью;
 *  - перед каждым запросом к серверу. Иначе обмен, переживший выход,
 *    продолжает слать строки прежней семьи — уже без токена, получает
 *    отказы политики и шумит;
 *  - на каждом отказе сервера. Отказ, пришедший после стирания на запрос,
 *    ушедший до него, не бросается наверх и не уходит в Sentry.
 *
 *  При расхождении обмен молча выходит: не бросает и не зовёт `report`.
 *  Стирание — решение взрослого, а не происшествие. */
function свой(снятая: number): boolean {
  return эпоха() === снятая;
}

/** Отказ, который не пройдёт НИКОГДА, сколько ни повторяй.
 *
 *  Перечислены поимённо и только те, что означают «эти данные неверны»:
 *
 *  - `U0001` — отбраковка невозможной отметки времени: раньше 2020 года или
 *    дальше суток в будущее, то есть занятия с планшета, у которого села
 *    батарейка и сбросились часы. Класс `U0` в Приложении A PostgreSQL не
 *    занят ничем и оставлен для своих кодов — а вот `P0002`, стоявший здесь
 *    сначала, ЗАНЯТ: это стандартный `no_data_found`, который поднимает
 *    любой `SELECT ... INTO STRICT`, не нашедший строки. То есть код,
 *    выбранный для «навсегда», означал бы «ничего не найдено» — самый
 *    временный случай из возможных;
 *  - `23514` и `23502` — нарушение проверки и NOT NULL: строка не станет
 *    годной от повтора.
 *
 *  **`P0001` в этом списке БЫЛ и был ошибкой.** Это код любого нашего
 *  `raise exception`, а не «плохая дата». Измерено на живой базе:
 *  журнал в несуществующий профиль тоже даёт `P0001` — а это ВРЕМЕННО,
 *  профиль просто ещё не доехал, и та же строка пройдёт, как только он
 *  появится. Пропуск по `P0001` выбросил бы её навсегда, и занятия не
 *  появились бы на втором устройстве при здоровом экране. Поэтому у
 *  отбраковки отметок теперь свой код, а `P0001` остался временным.
 *
 *  Всё остальное считается ВРЕМЕННЫМ намеренно. Предел запросов (429),
 *  протухший токен (401), пятисотка, отказ сериализации — по каждому из них
 *  повтор имеет смысл, а пропуск означал бы курсор, ушедший вперёд, и
 *  занятия, не уехавшие уже никогда. Причём на следующем же обмене экран
 *  показал бы полный порядок.
 *
 *  Ошибиться здесь легко в дешёвую сторону — «не сетевой, значит
 *  навсегда», — и цена ошибки в этой стороне: тихо потерянные занятия. */
function permanentReject(error: unknown): boolean {
  const code = String((error as { code?: string })?.code ?? '');
  return code === 'U0001' || code === '23514' || code === '23502';
}

/** Отсутствие связи — общая беда, а не беда одного ребёнка. Продолжать
 *  обмен остальными профилями бессмысленно: они упрутся в то же самое, а
 *  экран обязан сказать «нет связи», а не «профиль сломан». */
const offlineError = looksOffline;

/** Отказ, после которого повторять ту же пачку бессмысленно: профиля на
 *  сервере больше нет.
 *
 *  Различать это и сетевой отказ обязательно. Иначе два верных по
 *  отдельности решения складываются в тупик: родитель удалил профиль на
 *  планшете, телефон был оффлайн и держит непереданные попытки; телефон
 *  выходит в сеть, порядок обязывает начать с отправки, `with check` её
 *  отбивает — и докачка, которая одна могла бы сообщить телефону об
 *  удалении, не начинается никогда. */
function profileGone(error: { code?: string } | null): boolean {
  return error?.code === '42501' || error?.code === '23503' || error?.code === 'P0001';
}

function lookTaken(error: { code?: string } | null): boolean {
  return error?.code === '23505';
}

/** Строки одного профиля, записанные ЭТИМ устройством и ещё не отправленные.
 *
 *  Диапазон идёт по индексу с `deviceId` в префиксе, а не по наивному
 *  `seq > pushedUpTo`. Докачка кладёт в ту же таблицу строки других
 *  устройств, и часы одного из них могут уйти вперёд — планшет, пролежавший
 *  разряженным, приезжает с отметками из будущего. Наивная форма захватила
 *  бы их в пачку, сдвинула курсор на их максимум, и все последующие
 *  СОБСТВЕННЫЕ записи оказались бы ниже курсора и не уехали никогда. Молча:
 *  строки на устройстве есть, на других устройствах их нет, признака «не
 *  отправлено» в строке нет. */
async function unsentRange<T extends { id: string; seq: { ms: number; counter: number } }>(
  table: Dexie.Table<T, string>,
  deviceId: string,
  profileId: string,
  cursor: { ms: number; counter: number; id: string } | null,
  limit: number,
): Promise<T[]> {
  const lower = cursor
    ? [deviceId, profileId, cursor.ms, cursor.counter, cursor.id]
    : [deviceId, profileId];
  const upper = [deviceId, profileId, Dexie.maxKey];
  const range = table.where(PUSH_INDEX).between(lower, upper, false, true);
  return range.limit(limit).toArray();
}

async function cursorOf(profileId: string, table: SyncTable) {
  return db.syncState.get([profileId, table]);
}

/** Сколько тренировок ещё не уехало на сервер.
 *
 *  Нужно ровно одному месту — диалогу выхода, который обязан назвать цену
 *  ЧИСЛОМ. «Возможно, часть данных будет потеряна» родителю ничего не
 *  говорит, а стирание при выходе необратимо. */
/** Сколько занятий сервер не принял навсегда. Считается при каждом
 *  пересчёте — как карантин, и по той же причине: след обязан пережить не
 *  только этот обмен, но и все следующие. */
export async function countNotSent(): Promise<number> {
  return db.notSent.count();
}

export async function countUnsent(): Promise<number> {
  const device = await db.device.get('singleton');
  if (!device) return 0;
  const all = await db.profiles.toArray();
  const profiles = all.filter((p) => p.deletedAt === null);
  /** Правки самих профилей считаются наравне с тренировками, и это не
   *  придирка. Число показывается в диалоге выхода, а выход СТИРАЕТ
   *  устройство. Не сосчитанное здесь надгробие пропадает вместе с местной
   *  базой — и удалённый ребёнок, которому только что сказали «удалён
   *  навсегда», возвращается из облака при следующем входе.
   *
   *  Считаются и живые (смена облика), и мёртвые (надгробие): первые ушли бы
   *  молча, вторые — с обещанием, обратным правде. */
  let total = all.filter((p) => p.needsPush).length;
  for (const profile of profiles) {
    const state = await cursorOf(profile.id, 'sessions');
    const rows = await unsentRange(
      db.sessions, device.deviceId, profile.id, state?.pushedUpTo ?? null, Number.POSITIVE_INFINITY,
    );
    total += rows.length;
  }
  return total;
}

/** Строка журнала как она уезжает на сервер.
 *
 *  Уезжает целиком, и это осознанно. `deviceId` в плане назван ненужным для
 *  расчёта освоенности — но он первый компонент индекса отправки, и без
 *  него докачанная строка стала бы невидимой для отправки на СВОЁМ
 *  устройстве и видимой на чужом: каждое устройство пыталось бы отправить
 *  чужие строки вечно. Он не про ребёнка, а про то, кто записал. */
function payloadOf(row: object): Record<string, unknown> {
  return row as Record<string, unknown>;
}

/** Проверка строки, пришедшей с сервера.
 *
 *  Сервер строку не разбирает — `payload` для него непрозрачен. Значит
 *  единственное место, где можно поймать испорченную строку, здесь. Без
 *  проверки `NaN` в замере уехал бы в расчёт средних, а строка без часов
 *  выпала бы из обоих индексов и стала невидимой. */
function pullable(payload: unknown, profileId: string): Record<string, unknown> | null {
  if (!payload || typeof payload !== 'object') return null;
  const row = { ...(payload as Record<string, unknown>) };
  if (typeof row.id !== 'string' || row.id === '') return null;
  if (typeof row.deviceId !== 'string' || row.deviceId === '') return null;
  const seq = row.seq as { ms?: unknown; counter?: unknown } | undefined;
  if (!seq || !Number.isFinite(seq.ms) || !Number.isFinite(seq.counter)) return null;
  /** Профиль берётся из КОЛОНКИ, а не из содержимого. Иначе строка с чужим
   *  `profileId` внутри легла бы под другого ребёнка — при том что колонка
   *  проверена политикой, а содержимое не проверено ничем. */
  row.profileId = profileId;
  return row;
}

function tooNew(row: Record<string, unknown>): boolean {
  const version = row.schemaVersion;
  return typeof version === 'number' && version > SCHEMA_VERSION;
}

/** Отправка и докачка профилей.
 *
 *  Профили — единственная изменяемая сущность, и сервер по ним авторитетен.
 *  Порядок жёсткий: сначала отправка, потом докачка. Наоборот нельзя:
 *  родитель завёл ребёнка офлайн уже после входа, месяц занятий лежит на
 *  телефоне, отправка не прошла — и докачка, увидев список без этого
 *  профиля, снесла бы его вместе с занятиями. Локально удаляются только
 *  профили с отметкой `serverKnown`: те, о которых сервер точно знает. */
/** Отметить отправленным ровно то, что отправили.
 *
 *  Слепое `update(id, { needsPush: false })` теряло удаление НАСОВСЕМ, и
 *  дорога туда самая обыкновенная. Обмен запускается сам после тренировки;
 *  он читает список профилей и уходит в сеть. Пока он в сети, родитель
 *  открывает родительский раздел и удаляет ребёнка — местное надгробие
 *  ставится, `needsPush` становится истиной. Собственный обмен удаления при
 *  этом не идёт: предыдущий ещё не кончился, и `shouldSync` его не пускает.
 *  Затем возвращается первый обмен и гасит `needsPush` у строки, которая с
 *  тех пор СТАЛА НАДГРОБИЕМ.
 *
 *  Дальше тишина в обе стороны: на сервере ребёнок жив, локально его нет, и
 *  надгробие уже никогда не отправится — отправлять помечено нечего. На
 *  другом устройстве ребёнок остаётся, а на этом при следующей докачке
 *  ВОЗВРАЩАЕТСЯ. Родителю при этом сказали «удалён навсегда».
 *
 *  Поэтому гасим по сверке: если строка изменилась с момента чтения, флаг
 *  не трогаем — пусть уедет следующим обменом. */
async function markPushed(sent: ProfileRow, снятая: number): Promise<void> {
  await db.transaction('rw', db.profiles, async () => {
    if (!свой(снятая)) return;
    const fresh = await db.profiles.get(sent.id);
    if (!fresh) return;
    const same = fresh.deletedAt === sent.deletedAt
      && fresh.color === sent.color
      && fresh.animal === sent.animal;
    if (!same) return;
    await db.profiles.put({ ...fresh, serverKnown: true, needsPush: false });
  });
}

/** Что значит отказ сервера на отправку профиля. */
export type СмыслОтказаПрофиля = 'облик занят' | 'уже есть' | 'сеть' | 'застрял';

/** Толкование отказа на отправку профиля. Чистая: ни сети, ни базы — ответ
 *  на уточняющий вопрос приносит `syncProfiles`.
 *
 *  `вставка` — отказ пришёл на ВСТАВКУ (профиль серверу ещё не известен),
 *  а не на правку. `строкаЕсть` — ответ сервера на вопрос «есть ли у тебя
 *  строка с этим `id`»; `null` — не спрашивали или ответа нет. Если сам
 *  вопрос получил отказ, `syncProfiles` толкует здесь ЕГО отказ, а не
 *  исходный: отправку остановил он.
 *
 *  Код 23505 у профиля значит одно из трёх, и все три достижимы:
 *
 *  1. вставку отвергла занятая пара «цвет и зверёк» среди живых. Два
 *     устройства, заведшие офлайн одного и того же синего ежа, дали разные
 *     `id` при одинаковой паре;
 *  2. вставка упёрлась в СВОЮ ЖЕ строку: сервер узнал о профиле, а отметка
 *     `serverKnown` до нас не доехала — отправка прошла, ответ потерялся.
 *     Это не конфликт, а повод отправить то же правкой;
 *  3. правку отверг НОВЫЙ облик, занятый другим живым профилем. Строка при
 *     этом есть наверняка: правка несуществующей строки не конфликтует ни с
 *     чем, у PostgREST это успех с нулём строк.
 *
 *  Первый и второй смыслы различаются не разбором текста ошибки, а вопросом
 *  к серверу. Разбор текста сломался бы от переименования индекса, и
 *  сломался бы молча — а переименование уже было: ограничение
 *  `profiles_parent_id_color_animal_key` снято и заменено индексом
 *  `profiles_look_alive_idx` (`20260901120600_soft_delete.sql`).
 *
 *  «Уже есть» бывает только у вставки: правка, отвергнутая кодом 23505,
 *  никогда не зовёт повтор самой себя. */
export function толкованиеОтказаПрофиля(
  ошибка: unknown, вставка: boolean, строкаЕсть: boolean | null,
): СмыслОтказаПрофиля {
  if (lookTaken(ошибка as { code?: string } | null)) {
    if (!вставка) return 'облик занят';
    if (строкаЕсть === true) return 'уже есть';
    if (строкаЕсть === false) return 'облик занят';
    /** Ответа на вопрос нет — какой из смыслов, 1 или 2, неизвестно, и
     *  конфликт облика НЕ заявляется. Прежде заявлялся, и родителю на
     *  один обмен говорили «такой цвет и зверёк уже заняты» про облик,
     *  который никому не мешал (карточка 7б). */
    return 'застрял';
  }
  if (offlineError(ошибка)) return 'сеть';
  return 'застрял';
}

async function syncProfiles(
  parentId: string, outcome: SyncOutcome, снятая: number,
): Promise<void> {
  /** Первое обращение к базе за весь обмен — и самое частое место, где
   *  Safari успевает оборвать соединение за время в фоне (раздел
   *  «Синхронизация», iPhone; Sentry MULT-TRAINER-9). Чтение чистое,
   *  поэтому повтор безопасен: если он восстановит соединение, весь
   *  остальной обмен пойдёт по уже здоровому `db.idbdb`, и до `pushTable`/
   *  `pullTable` поломка вообще не доедет. */
  const local = await сПовторомПриОбрыве(() => db.profiles.orderBy('createdAt').toArray());
  const client = supabase();

  /** Отправляются только профили с местными изменениями. Слепая отправка
   *  всех переписывала бы серверную строку устаревшей копией: смена цвета,
   *  сделанная на планшете, отменялась бы первой же синхронизацией
   *  телефона. */
  /** Кого отправили в ЭТОМ обмене. Их серверный облик ниже не применяется,
   *  и это не перестраховка.
   *
   *  Отправка сняла признак (`markPushed`), а чтение списка идёт следом —
   *  и может прийти с отставшей реплики, где лежит ПРЕЖНИЙ облик. Тогда
   *  свежеотправленная правка откатывается к старому значению, причём
   *  признака отправки на строке уже нет: правка не уедет никогда. То есть
   *  ровно та же тихая потеря, что чинилась выше, только другой дорогой —
   *  и внесённая этой самой починкой. Найдено внешним ревью. */
  const отправленные = new Set<string>();

  for (const profile of local.filter((p) => p.needsPush)) {
    /** Профиль отправляется ВСТАВКОЙ или ПРАВКОЙ, а не upsert-ом.
     *
     *  Это не стилистика. PostgREST собирает список `on conflict do update
     *  set` из всех колонок запроса, а Postgres проверяет право `UPDATE` на
     *  каждую колонку этого списка ЕЩЁ ПРИ ПЛАНИРОВАНИИ — независимо от
     *  того, случится конфликт или нет. У родителя право есть только на
     *  `color`, `animal` и `deleted_at`; `id`, `parent_id` и `created_at`
     *  ему трогать нечем, и правильно.
     *
     *  Поэтому upsert отказывал ВСЕГДА, даже когда строки на сервере ещё
     *  нет. Измерено на живом бою 2026-09-02: `42501 permission denied for
     *  table profiles` при первом же входе — то есть профили не уезжали
     *  вовсе, а вместе с ними и журналы. Воспроизведено на деве тем же
     *  запросом, что шлёт клиент.
     *
     *  Изменяемых полей у профиля ровно три, и правка шлёт только их. */
    const mutable = {
      color: profile.color,
      animal: profile.animal,
      deleted_at: profile.deletedAt === null ? null : new Date(profile.deletedAt).toISOString(),
    };
    if (!свой(снятая)) return;
    const { error } = profile.serverKnown
      ? await client.from('profiles').update(mutable).eq('id', profile.id)
      : await client.from('profiles').insert({
          id: profile.id,
          parent_id: parentId,
          created_at: new Date(profile.createdAt).toISOString(),
          ...mutable,
        });
    if (error) {
      if (!свой(снятая)) return;
      const вставка = !profile.serverKnown;
      /** Отказ, остановивший отправку профиля, — его и толкуем ниже.
       *  Сначала это отказ самой отправки. Если свой отказ получили
       *  уточняющий вопрос или повтор правкой — уже он: отправку остановил
       *  он, и наверх или в Sentry уходит именно он. */
      let отказ: unknown = error;
      /** Уточняющий вопрос «есть ли у сервера строка с этим `id`» задаётся,
       *  только когда его ответ меняет толкование. Каким отказам он нужен,
       *  знает одна `толкованиеОтказаПрофиля` — сегодня это 23505 на
       *  вставке. Правке он не нужен: отвергнутая правка сама доказала, что
       *  строка есть. Прежде вопрос и повтор той же правки шли и тут, и
       *  ничего не меняли: правка падала вторично тем же 23505. */
      let строкаЕсть: boolean | null = null;
      if (толкованиеОтказаПрофиля(error, вставка, true)
          !== толкованиеОтказаПрофиля(error, вставка, false)) {
        const { data: existing, error: неОтветил } = await client
          .from('profiles')
          .select('id')
          .eq('id', profile.id)
          .maybeSingle();
        if (неОтветил) {
          if (!свой(снятая)) return;
          /** Вопрос без ответа — НЕ «облик занят» (карточка 7б). Прежде
           *  ответ «нет» и отказ сливались, и протухший токен на вопросе
           *  давал родителю «такой цвет и зверёк уже заняты». */
          отказ = неОтветил;
        } else {
          строкаЕсть = existing !== null;
        }
      }
      let смысл = толкованиеОтказаПрофиля(отказ, вставка, строкаЕсть);
      if (смысл === 'уже есть') {
        if (!свой(снятая)) return;
        const { error: failed } = await client
          .from('profiles')
          .update(mutable)
          .eq('id', profile.id);
        if (!failed) {
          await markPushed(profile, снятая);
          /** Отправлен в ЭТОМ обмене — и его облик из списка, прочитанного
           *  ниже, не применяется, как у обычной отправки: довод у
           *  `отправленные`. Без этого список с отставшей реплики клал
           *  прежний облик поверх свежей правки, а признак отправки уже
           *  снят (карточка 7б). */
          отправленные.add(profile.id);
          continue;
        }
        if (!свой(снятая)) return;
        /** Повтор правкой, а строка есть — значит, 23505 здесь только
         *  третьего смысла: НОВЫЙ облик занят другим живым профилем.
         *  Прочие отказы повтора разбираются ниже так же, как отказ первой
         *  отправки. Прежде они бросались наверх и останавливали обмен
         *  всей семьи (карточка 7б). */
        отказ = failed;
        смысл = толкованиеОтказаПрофиля(failed, false, true);
      }
      /** Занятая пара — не сетевой отказ, и повторять её бесполезно: пока
       *  профиль не принят сервером, весь журнал этого устройства не
       *  уезжает. Выбор за родителем. */
      if (смысл === 'облик занят') {
        outcome.lookConflicts.push(profile.id);
        continue;
      }
      /** Сеть — общая беда, её наверх. Всё остальное — беда ОДНОГО
       *  профиля, и ронять из-за неё обмен всей семьи нельзя: тринадцатый
       *  профиль, заведённый офлайн, иначе заморозил бы занятия у всех
       *  остальных детей навсегда, и причина не была бы названа нигде. */
      if (смысл === 'сеть') throw отказ;
      report(отказ, 'syncProfiles');
      outcome.stuck.push(profile.id);
      /** Отметки об отправке тут НЕ ставим, и это не мелочь.
       *
       *  Прежде она стояла ниже, за пределами разбора отказа, — то есть
       *  профиль помечался отправленным ДАЖЕ КОГДА ОТПРАВКА НЕ УДАЛАСЬ.
       *  Дальше начиналось самое неприятное: `serverKnown` становился
       *  истиной, и следующая попытка шла ПРАВКОЙ вместо вставки, а правка
       *  несуществующей строки у PostgREST — это успех с нулём затронутых
       *  строк. То есть профиль не появлялся на сервере никогда, а обмен
       *  каждый раз рапортовал об успехе. Журнал этого ребёнка при этом не
       *  уезжал вовсе: без принятого профиля сервер отвергает его строки.
       *
       *  Для удаления это означало «удалён навсегда» при живом ребёнке на
       *  сервере — то есть ровно ту поломку, которую чинила сверка выше,
       *  только другой дорогой. */
      continue;
    }
    await markPushed(profile, снятая);
    отправленные.add(profile.id);
  }

  if (!свой(снятая)) return;
  const { data, error } = await client
    .from('profiles')
    .select('id, color, animal, created_at, deleted_at');
  if (error) {
    if (!свой(снятая)) return;
    throw error;
  }

  const server = new Map((data ?? []).map((row) => [row.id as string, row]));
  for (const row of server.values()) {
    const id = row.id as string;
    const known = local.find((p) => p.id === id);
    const deletedAt = row.deleted_at ? Date.parse(row.deleted_at as string) : null;
    if (deletedAt !== null) {
      if (known && known.deletedAt === null) {
        await wipeProfileLocally(id, deletedAt, снятая);
        outcome.removed.push(id);
      }
      continue;
    }
    if (!known) {
      await db.transaction('rw', db.profiles, async () => {
        if (!свой(снятая)) return;
        await db.profiles.put({
          id,
          color: row.color as string,
          animal: row.animal as string,
          createdAt: Date.parse(row.created_at as string),
          serverKnown: true,
          deletedAt: null,
          needsPush: false,
          schemaVersion: SCHEMA_VERSION,
        });
      });
      continue;
    }
    /** Облик берётся с сервера: он авторитетен по профилям, а смена цвета —
     *  единственное, что вообще меняется.
     *
     *  Свою неотправленную правку затирать нельзя: она уедет следующей же
     *  отправкой, и до тех пор местная копия авторитетнее.
     *
     *  СВЕРКА ИДЁТ ПО СВЕЖЕЙ СТРОКЕ, А НЕ ПО СНИМКУ, и это та же болезнь,
     *  что чинил `markPushed`. `known` взят из `local` — снимка, сделанного
     *  ДО отправки и до чтения сервера. Родитель, сменивший ребёнку облик,
     *  пока обмен шёл в сети, имеет в снимке `needsPush === false`, а на
     *  сервере лежит ПРЕЖНИЙ облик: условие срабатывало, и свежая правка
     *  затиралась старым значением. Дальше хуже: признак отправки у строки
     *  остаётся, и следующая отправка увозит на сервер серверные же
     *  значения — правка родителя исчезает молча и навсегда.
     *
     *  Найдено мутационным проходом по слою данных: мутация `&&` → `||`
     *  пережила прогон, а написанный под неё тест покраснел на НЕТРОНУТОМ
     *  коде. */
    /** Свою же только что отправленную правку не трогаем: см. довод у
     *  `отправленные`. */
    if (отправленные.has(id)) continue;
    await db.transaction('rw', db.profiles, async () => {
      if (!свой(снятая)) return;
      const fresh = await db.profiles.get(id);
      /** Проверка надгробия здесь защитная: серверный список на этой ветке
       *  содержит только живых, а местное надгробие либо не отправлено
       *  (тогда его держит `needsPush`), либо уже отправлено — и тогда
       *  сервер отдал бы его в другой ветке. Достижимого пути нет; строка
       *  стоит потому, что цена ошибки — облик, применённый к удалённому
       *  ребёнку, а цена строки — ничто. */
      if (!fresh || fresh.needsPush || fresh.deletedAt !== null) return;
      if (fresh.color === row.color && fresh.animal === row.animal) return;
      await db.profiles.update(id, { color: row.color as string, animal: row.animal as string });
    });
  }

  for (const profile of local) {
    if (profile.deletedAt === null && profile.serverKnown && !server.has(profile.id)) {
      await wipeProfileLocally(profile.id, Date.now(), снятая);
      outcome.removed.push(profile.id);
    }
  }
}

/** Снос занятий профиля по решению сервера. Отличается от `deleteProfile`
 *  тем, что не спрашивает и не ставит своё время: отметка приходит оттуда,
 *  где родитель нажал «удалить навсегда». */
async function wipeProfileLocally(
  profileId: string, deletedAt: number, снятая: number,
): Promise<void> {
  const tables = [
    db.profiles, db.attempts, db.reveals, db.sessions, db.state, db.syncState, db.quarantine,
    db.notSent,
  ];
  await db.transaction('rw', tables, async () => {
    if (!свой(снятая)) return;
    const own = (row: { profileId: string }) => row.profileId === profileId;
    await db.attempts.filter(own).delete();
    await db.reveals.filter(own).delete();
    await db.sessions.filter(own).delete();
    await db.state.delete(profileId);
    await db.syncState
      .where('[profileId+table]')
      .between([profileId], [profileId, Dexie.maxKey])
      .delete();
    await db.quarantine.where('profileId').equals(profileId).delete();
    /** След непринятых уходит вместе с ребёнком: строк, о которых он
     *  говорит, больше нет, и держать его значило бы показывать родителю
     *  число, за которым ничего не стоит. */
    await db.notSent.where('profileId').equals(profileId).delete();
    const profile = await db.profiles.get(profileId);
    if (profile) {
      await db.profiles.put({ ...profile, deletedAt, serverKnown: true, needsPush: false });
    }
  });
}

function tableOf(name: SyncTable) {
  if (name === 'attempts') return db.attempts;
  if (name === 'reveals') return db.reveals;
  return db.sessions;
}

async function pushTable(
  parentId: string, deviceId: string, profileId: string, name: SyncTable, outcome: SyncOutcome,
  снятая: number,
): Promise<void> {
  const client = supabase();
  /** Ребёнок, удалённый, пока отправка шла по сети, своих строк больше не
   *  получает — ни курсора отправки, ни следа непринятых.
   *
   *  Путь обыкновенный: обмен после тренировки отправляет занятия, родитель
   *  жмёт «удалить навсегда», `deleteProfile` ставит надгробие и стирает
   *  его занятия, — и ответ сервера (там ребёнок ещё жив) дописывал курсор,
   *  строку-сироту, которую никто не прочтёт и не сотрёт, а на отвергнутую
   *  строку — след непринятых и событие в Sentry. Этот след родитель
   *  ВИДЕЛ: «Часть занятий сервер не принял…» про ребёнка, которого уже
   *  нет, и держался он до стирания устройства. Измерено исполнением:
   *  `tests/data/lifecycle.test.ts`.
   *
   *  Мерка та же, что у докачки и `saveSession`, и читается ВНУТРИ
   *  транзакции записи: профиль обязан существовать и не быть надгробием.
   *  Существования мало — удалённый профиль остаётся строкой-надгробием.
   *  Эпоха этого не ловит: устройство не стирали, обмен свой. */
  const живой = async (): Promise<boolean> => {
    const профиль = await db.profiles.get(profileId);
    return профиль !== undefined && профиль.deletedAt === null;
  };
  for (;;) {
    const state = await cursorOf(profileId, name);
    const rows = await unsentRange(
      tableOf(name) as unknown as Dexie.Table<AttemptRow | RevealRow | SessionRow, string>,
      deviceId, profileId, state?.pushedUpTo ?? null, BATCH,
    );
    if (rows.length === 0) return;

    const toRow = (row: AttemptRow | RevealRow | SessionRow) => ({
      id: row.id,
      parent_id: parentId,
      profile_id: profileId,
      payload: payloadOf(row),
    });
    const options = { onConflict: 'parent_id,profile_id,id', ignoreDuplicates: true } as const;

    if (!свой(снятая)) return;
    const { error } = await client.from(name).upsert(rows.map(toRow), options);
    if (error) {
      if (!свой(снятая)) return;
      /** Сеть — общая беда, её наверх: там она станет «нет связи», а не
       *  «строки испорчены». */
      if (offlineError(error)) throw error;
      /** Отвергнуть НАВСЕГДА может только проверка данных, и такие отказы
       *  перечислены поимённо. Всё прочее — предел запросов, протухший
       *  токен, пятисотка, отказ сериализации — временно, и пропускать по
       *  ним строки нельзя: курсор ушёл бы вперёд, а занятия не уехали бы
       *  уже никогда, при здоровом экране на следующем же обмене.
       *
       *  Здесь легко ошибиться в дешёвую сторону: «не сетевой — значит
       *  навсегда». Это неверно, и цена ошибки — тихо потерянные занятия.
       *
       *  Ребёнка уже нет — временный отказ не повод ставить его в `stuck`.
       *  Строка сказала бы «занятия не отправляются» про удалённого, и
       *  событие ушло бы в Sentry; следующего обмена по нему не будет. */
      if (!permanentReject(error)) {
        if (!(await живой())) return;
        throw error;
      }
      /** Пачку отверг сервер по проверке данных. Виновата одна строка или
       *  несколько, но какие именно — из ответа не видно, поэтому пробуем
       *  поштучно. Дорого, но случается это редко, а альтернатива —
       *  замороженный навсегда журнал ребёнка. */
      for (const row of rows) {
        if (!свой(снятая)) return;
        const single = await client.from(name).upsert([toRow(row)], options);
        if (!single.error) continue;
        /** Отдельная строка тоже может упасть временно — тогда наверх, а
         *  не в пропуск: курсор не двинется, и следующий обмен повторит. */
        if (!permanentReject(single.error)) {
          if (!свой(снятая)) return;
          if (!(await живой())) return;
          throw single.error;
        }
        /** След ПОСТОЯННЫЙ, а не только в ответе этого обмена.
         *
         *  Без него число жило один обмен: следующий давал ноль, строка на
         *  экране исчезала, а занятий на сервере так и не появлялось.
         *  Родитель, не посмотревший в тот раз, не узнавал никогда.
         *
         *  Число в ответе и событие в Sentry — только если след лёг: след
         *  о занятиях прежней семьи на стёртом устройстве и о занятиях
         *  удалённого ребёнка не пишется, и сообщать тогда не о чем. Не
         *  легший след кончает и отправку таблицы: слать остаток пачки
         *  незачем, курсор не встанет всё равно. */
        const записан = await db.transaction('rw', [db.notSent, db.profiles], async () => {
          if (!свой(снятая)) return false;
          if (!(await живой())) return false;
          await db.notSent.put({
            table: name,
            id: row.id,
            profileId,
            reason: String((single.error as { message?: string }).message ?? single.error),
            atMs: Date.now(),
          });
          return true;
        });
        if (!записан) return;
        outcome.notSent += 1;
        /** Наружу — потому что это не норма, а сбитая дата на устройстве
         *  или расхождение проверок клиента и сервера; узнать об этом
         *  можно только отсюда. */
        report(single.error, `pushTable:${name}`);
      }
    }

    const last = rows[rows.length - 1];
    const сдвинут = await db.transaction('rw', [db.syncState, db.profiles], async () => {
      if (!свой(снятая)) return false;
      if (!(await живой())) return false;
      await db.syncState.put({
        profileId,
        table: name,
        pulledUpTo: state?.pulledUpTo ?? 0,
        pushedUpTo: { ms: last.seq.ms, counter: last.seq.counter, id: last.id },
      });
      return true;
    });
    /** Курсор не встал — устройство стёрто или ребёнок удалён, — и
     *  отправлять дальше незачем. Счёт ответа — по тому, что легло, как у
     *  докачки. Без выхода полная пачка, не сдвинувшая курсора, звала бы
     *  ту же пачку снова; сейчас круг не замыкается лишь потому, что то же
     *  стирание уносит и сами строки, — держаться на этом незачем. */
    if (!сдвинут) return;
    outcome.pushed += rows.length;
    if (rows.length < BATCH) return;
  }
}

async function pullTable(
  profileId: string, name: SyncTable, outcome: SyncOutcome, снятая: number,
): Promise<void> {
  const client = supabase();
  for (;;) {
    const state = await cursorOf(profileId, name);
    const from = state?.pulledUpTo ?? 0;
    /** Начальный курсор — ноль, а не текущий счётчик профиля.
     *
     *  Соблазн взять `attempts_seq` со строки профиля выглядит экономией
     *  одного запроса, а стоит всей истории ребёнка: новый планшет семьи
     *  начал бы с конца и никогда не запросил бы то, что было до него.
     *  Счётчик в профиле — для ВЫДАЧИ номеров, а не для курсора читателя. */
    if (!свой(снятая)) return;
    const { data, error } = await client
      .from(name)
      .select('server_seq, payload')
      .eq('profile_id', profileId)
      .gt('server_seq', from)
      .order('server_seq', { ascending: true })
      .limit(BATCH);
    if (error) {
      if (!свой(снятая)) return;
      throw error;
    }
    const page = data ?? [];
    if (page.length === 0) return;

    const accepted: Record<string, unknown>[] = [];
    const quarantined: QuarantineRow[] = [];
    let upTo = from;
    let stopped = false;
    for (const item of page) {
      const row = pullable(item.payload, profileId);
      if (row && tooNew(row)) {
        /** Курсор НЕ двигается за эту строку. Пропустить её значит потерять
         *  навсегда: второй раз её никто не предложит. */
        stopped = true;
        break;
      }
      if (row) {
        accepted.push(row);
      } else {
        quarantined.push({
          table: name,
          profileId,
          serverSeq: Number(item.server_seq),
          payload: item.payload,
          reason: 'строка не прошла проверку при докачке',
          atMs: Date.now(),
        });
      }
      upTo = Number(item.server_seq);
    }

    /** Страница, карантин и курсор ложатся ОДНОЙ транзакцией.
     *
     *  Не «сначала строки, потом курсор» — это тоже работало бы, но
     *  доказывать пришлось бы, что обещание записи разрешается по
     *  завершению транзакции, а не по успеху запроса. Одна транзакция
     *  снимает вопрос целиком: либо страница применена и курсор сдвинут,
     *  либо не произошло ничего и та же страница приедет снова. */
    const применена = await db.transaction(
      'rw', [tableOf(name), db.quarantine, db.syncState, db.profiles], async () => {
        if (!свой(снятая)) return false;
        /** Ребёнок, удалённый, пока страница шла по сети, своих строк больше
         *  не получает — ни журнала, ни карантина, ни курсора.
         *
         *  Эпоха этого не ловит: устройство не стирали, обмен свой. А путь
         *  прямой: обмен прочитал у сервера живой профиль и тянет его журнал,
         *  родитель в это время жмёт «удалить навсегда», `deleteProfile`
         *  ставит надгробие и стирает занятия, — и ответ сервера кладёт их
         *  обратно. На экранах их нет (экраны читают живых), а убрать их
         *  потом некому: `syncProfiles` сносит локально только профиль, у
         *  нас ещё живой, а этот уже надгробие. Строки оставались навсегда,
         *  хотя родителю сказано, что удаление стирает занятия с устройства
         *  сразу и необратимо. Измерено исполнением:
         *  `tests/data/lifecycle.test.ts`.
         *
         *  Мерка та же, что у `saveSession`, и по той же причине читается
         *  ВНУТРИ транзакции: профиль обязан существовать и не быть
         *  надгробием. Существования мало — удалённый профиль остаётся
         *  строкой-надгробием. */
        const профиль = await db.profiles.get(profileId);
        if (!профиль || профиль.deletedAt !== null) return false;
        if (accepted.length > 0) {
          await (tableOf(name) as unknown as Dexie.Table<Record<string, unknown>, string>).bulkPut(
            accepted,
          );
        }
        if (quarantined.length > 0) await db.quarantine.bulkPut(quarantined);
        if (upTo > from) {
          const fresh = await db.syncState.get([profileId, name]);
          await db.syncState.put({
            profileId,
            table: name,
            pulledUpTo: upTo,
            pushedUpTo: fresh?.pushedUpTo ?? null,
          });
        }
        return true;
      },
    );
    /** Страница не легла — устройство стёрто или ребёнок удалён, — и
     *  читать дальше незачем. Выход здесь несущий: курсор удалённого
     *  ребёнка стёрт вместе с журналом, и без выхода полная страница, не
     *  легшая в базу, звала бы следующую с нуля — ту же самую, — и так без
     *  конца.
     *
     *  Ответ и события — по тому, что ЛЕГЛО, а не по тому, что пришло.
     *  Страница, пришедшая после стирания, не применена, и сообщать о ней
     *  нечего: иначе Sentry получал бы строки карантина прежней семьи, а
     *  экран после выхода — «обновите приложение» по чужому журналу. */
    if (!применена) return;
    if (stopped) outcome.needsUpdate = true;
    outcome.rejected += quarantined.length;
    outcome.pulled += accepted.length;
    for (const held of quarantined) {
      /** Не «на всякий случай»: строка есть на сервере, а в журнал не
       *  попала. Это происшествие, и разбирать его будем по карантину. */
      report(
        new Error(`строка ${name} № ${String(held.serverSeq)} не прошла проверку при докачке`),
        'pullTable',
      );
    }
    if (stopped || page.length < BATCH) return;
  }
}

/** Полный обмен: профили, затем журналы каждого живого ребёнка.
 *
 *  Отправка изолирована по профилю: отказ у одного ребёнка не должен
 *  останавливать синхронизацию остальных — иначе один сломанный профиль
 *  замораживает занятия всей семьи. */
export async function syncAll(parentId: string): Promise<SyncOutcome> {
  /** Эпоха снимается ПЕРВЫМ действием, до всякого ожидания: стирание,
   *  случившееся на первом же `await`, обязано быть для этого обмена уже
   *  чужим. */
  const снятая = эпоха();
  const outcome = empty();
  /** Первое обращение к базе во всём `syncAll` — и, как оказалось (М5), не
   *  менее частое место обрыва, чем первое чтение `syncProfiles`. Тесты
   *  обмена подсаживали обрыв только на курсорах (`подсадитьОбрывКурсора`),
   *  а это точечный `get` — своя дорога, и без обёртки обрыв здесь ронял
   *  `syncAll` ДВАЖДЫ ПОДРЯД: `syncProfiles` восстанавливал соединение на
   *  СВОЁМ чтении, но было уже поздно — это чтение падало раньше него. */
  const device = await сПовторомПриОбрыве(() => db.device.get('singleton'));
  if (!device) throw new Error('строки устройства нет');

  await syncProfiles(parentId, outcome, снятая);

  /** Профиль, которого сервер не принял, журналы не отправляет: они
   *  упрутся в тот же отказ, а курсор не сдвинется. */
  const notAccepted = new Set([...outcome.lookConflicts, ...outcome.stuck]);
  const profiles: ProfileRow[] = (await db.profiles.orderBy('createdAt').toArray()).filter(
    (p) => p.deletedAt === null && !notAccepted.has(p.id),
  );

  for (const profile of profiles) {
    /** Снято ДО работы над профилем, а не в `catch`: к моменту отказа кто-то
     *  другой (параллельное чтение, `проверитьСоединение` при возврате
     *  вкладки) мог уже переоткрыть то же самое соединение. Передаём это
     *  значение в `переоткрытьСоединение` ниже, а не зовём её без довода —
     *  иначе на одном обрыве, пойманном сразу в нескольких местах, было бы
     *  несколько закрытий вместо одного (М1). */
    const поколениеПрофиля = текущееПоколение();
    try {
      for (const name of TABLES) {
        try {
          await pushTable(parentId, device.deviceId, profile.id, name, outcome, снятая);
          await pullTable(profile.id, name, outcome, снятая);
        } catch (error) {
          /** Профиля больше нет — это ответ, а не отказ. Повторять пачку
           *  бессмысленно, а вот перечитать авторитетный список нужно: он и
           *  снесёт локальную копию. */
          if (!profileGone(error as { code?: string })) throw error;
          await syncProfiles(parentId, outcome, снятая);
          break;
        }
      }
    } catch (error) {
      /** Отказ ОДНОГО ребёнка не останавливает остальных.
       *
       *  Иначе одна испорченная строка или один упрямый отказ сервера
       *  замораживает занятия всей семьи: у второго ребёнка всё в порядке,
       *  а его тренировки не уезжают — и причина не названа нигде. Сеть —
       *  исключение: она сломана для всех сразу, и экран обязан сказать
       *  именно это. */
      if (offlineError(error)) throw error;
      /** Соединение с IndexedDB, оборванное Safari посреди обмена, бьёт по
       *  ОДНОМУ профилю так же, как испорченная строка или упрямый отказ
       *  сервера, — этот ребёнок встаёт в `stuck`, остальные продолжают.
       *  Но, в отличие от испорченной строки, обрыв не пройдёт сам:
       *  `db.idbdb` у Dexie остаётся тем же самым, ЖИВЫМ с её точки зрения,
       *  соединением, и без переоткрытия здесь СЛЕДУЮЩИЙ профиль в этом же
       *  цикле — и вообще весь следующий обмен — упёрся бы в тот же самый
       *  отказ навсегда, до перезагрузки страницы. Переоткрытие — не
       *  повтор ЭТОГО профиля (он уже показан пропавшим родителю через
       *  `stuck` и учтён этим самым `report` ниже, как и любой другой его
       *  отказ), а подготовка здоровой базы для следующего. */
      if (отказСоединения(error)) переоткрытьСоединение(поколениеПрофиля);
      report(error, 'syncProfile');
      outcome.stuck.push(profile.id);
    }
  }
  return outcome;
}

export { READ_INDEX };

/** Забыть, что докачано, и перечитать журналы с нуля.
 *
 *  Восстановление на случай, которого курсор не покрывает по построению:
 *  строка применена, курсор ушёл вперёд, а потом местная строка пропала —
 *  ошибкой в коде, чисткой одной таблицы, наполовину прошедшим обновлением.
 *  Обычная докачка её не вернёт никогда, потому что второй раз сервер её не
 *  предложит.
 *
 *  Стоит дёшево и безопасно: строки ложатся `put`-ом по своим `id`, поэтому
 *  повторное чтение всего журнала ничего не задваивает. Карантин чистится
 *  заодно — его содержимое приедет снова и, возможно, уже пройдёт проверку. */
export async function forgetPullCursors(): Promise<void> {
  await db.transaction('rw', [db.syncState, db.quarantine], async () => {
    const rows = await db.syncState.toArray();
    await db.syncState.bulkPut(rows.map((row) => ({ ...row, pulledUpTo: 0 })));
    await db.quarantine.clear();
  });
}

/** Сколько строк лежит в карантине. Показывается родителю: строка есть на
 *  сервере, а в журнале её нет, и молчать об этом нельзя. */
export async function countQuarantined(): Promise<number> {
  return db.quarantine.count();
}

/** Ждать не дольше `ms`.
 *
 *  Опоздавшая работа не отменяется: она доделывается в фоне, а её поздний
 *  отказ гасится здесь, чтобы не стать необработанным. Отказ, пришедший
 *  вовремя, уходит вызывающему как обычно. */
async function within<T>(
  work: Promise<T>,
  ms: number,
): Promise<{ done: true; value: T } | { done: false }> {
  const settled = work.then((value) => ({ done: true as const, value }));
  settled.catch(() => {});
  let timer: ReturnType<typeof setTimeout> | undefined;
  const late = new Promise<{ done: false }>((resolve) => {
    timer = setTimeout(() => resolve({ done: false }), ms);
  });
  try {
    return await Promise.race([settled, late]);
  } finally {
    clearTimeout(timer);
  }
}

/** Занят ли облик на сервере другим живым ребёнком этой семьи.
 *
 *  Спрашивается в момент заведения и смены облика. Местная проверка видит
 *  только то, что уже лежит на устройстве, а занятый облик мог появиться на
 *  сервере с другого устройства. Тогда профиль заводился, ребёнок занимался,
 *  и лишь следующий обмен сообщал, что сервер профиль не принял и занятия не
 *  уезжают. Строка обмена осталась запасной — для облика, занятого без связи.
 *
 *  Чужие семьи в ответ не попадают: чтение ограничено политикой строк, это
 *  тот же запрос, каким обмен читает список профилей.
 *
 *  `null` — ответа нет: нет связи, сервер отказал или молчит дольше
 *  `timeoutMs`. Тогда решает местная проверка: запирать заведение ребёнка
 *  из-за метро нельзя. */
export async function lookTakenOnServer(
  look: { color: string; animal: string },
  exceptId: string | null,
  timeoutMs = 5000,
): Promise<boolean | null> {
  const controller = new AbortController();
  try {
    const answer = await within(
      Promise.resolve(
        supabase()
          .from('profiles')
          .select('id, color, animal, deleted_at')
          .abortSignal(controller.signal),
      ),
      timeoutMs,
    );
    if (!answer.done) {
      controller.abort();
      return null;
    }
    const { data, error } = answer.value;
    if (error) {
      if (!offlineError(error)) report(error, 'lookTakenOnServer');
      return null;
    }
    return (data ?? []).some(
      (row) => row.deleted_at === null
        && row.id !== exceptId
        && row.color === look.color
        && row.animal === look.animal,
    );
  } catch (error) {
    if (!offlineError(error)) report(error, 'lookTakenOnServer');
    return null;
  }
}

/** Список детей семьи с сервера — без журналов.
 *
 *  Для одного случая: родитель вошёл на устройстве, где нет ни одного
 *  профиля. Обычный обмен живёт внутри готового шлюза профилей и на пустом
 *  устройстве не начинается, поэтому дети с сервера не появлялись, пока
 *  родитель не заведёт нового, — а заводил он обычно синего ежа, который по
 *  умолчанию стоит на всех устройствах и на сервере уже занят.
 *
 *  Тот же проход, что у обмена, а не второй разбор ответа: правила «сначала
 *  отправка, потом докачка» и надгробий закреплены на нём. Журналы привезёт
 *  обычный обмен, как только шлюз откроется.
 *
 *  `false` — не дождались за `timeoutMs`; проход доделывается в фоне. */
export async function syncProfileList(parentId: string, timeoutMs = 8000): Promise<boolean> {
  /** Своя эпоха, снятая так же первым действием: опоздавший проход
   *  доделывается в фоне, и выход, случившийся за это время, для него
   *  ровно так же чужой, как для обмена. */
  const answer = await within(syncProfiles(parentId, empty(), эпоха()), timeoutMs);
  return answer.done;
}
